import styled from '@emotion/styled';
import {Spacer, spacing} from '@nfq/react-grid';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';

import {Headline} from 'UI/components/layout/Text';
import {WaypointCard} from 'UI/components/modules/WaypointCard';

import {useNearbyWaypoints} from 'UI/hooks/useNearbyWaypoints';

/** Per-item stagger delay (in seconds) applied to the reveal animation, multiplied by the item's index. */
const STAGGER_DELAY_SECONDS = 0.08;

/**
 * The props for the `NearbyWaypoints` component.
 */
interface ComponentProps {
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'NearbyWaypoints'
     */
    testId?: string;
}

/**
 * Shows the waypoints nearest to the user's current location, sorted by ascending distance
 * (computed and sorted by MongoDB's geospatial `near` query, not in the browser) and revealed
 * in pages via a "load more" button. A switch lets the user search only the first waypoint of
 * each walk (the default), or every waypoint of every walk. Each result shows the walk's title
 * alongside it. Renders nothing until the user has enabled location sharing and a position fix
 * is available.
 *
 * @param props        The component props.
 * @param props.testId An optional test ID used for automation selectors. Defaults to `'NearbyWaypoints'`.
 * @returns A JSX element rendering the NearbyWaypoints section, or `null` while no user location is available.
 *
 * @example
 * ```tsx
 * <NearbyWaypoints />
 * ```
 */
const NearbyWaypoints = ({testId = 'NearbyWaypoints'}: ComponentProps) => {
    const {
        hasMore,
        isLoading,
        isVisible,
        loadMore,
        setShowAllWaypoints,
        showAllWaypoints,
        t,
        waypoints
    } = useNearbyWaypoints();

    if (!isVisible) return null;

    return (
        <Wrapper data-cy={testId}>
            <Spacer y={5} />
            <Header>
                <SectionHeading $size="headline" as="h2">{t('nearbyWaypoints.heading')}</SectionHeading>
                <ModeSwitchRow>
                    <ModeLabel>{t('nearbyWaypoints.modeAll')}</ModeLabel>
                    <ToggleTrack
                        $active={showAllWaypoints}
                        aria-checked={showAllWaypoints}
                        data-cy={`${testId}-mode-toggle`}
                        role="switch"
                        type="button"
                        onClick={() => setShowAllWaypoints(!showAllWaypoints)}
                    >
                        <ToggleThumb $active={showAllWaypoints} />
                    </ToggleTrack>
                </ModeSwitchRow>
            </Header>
            <List>
                <AnimatePresence
                    key={showAllWaypoints ? 'all' : 'start'}
                    initial={false}
                    mode="popLayout"
                >
                    {waypoints.map((waypoint, index) => (
                        <motion.div
                            key={waypoint.id}
                            exit={{
                                height: 0,
                                marginBottom: 0,
                                opacity: 0
                            }}
                            initial={{
                                opacity: 0,
                                y: 24
                            }}
                            layout="position"
                            transition={{
                                default: {
                                    delay: index * STAGGER_DELAY_SECONDS,
                                    duration: 0.4,
                                    ease: 'easeOut'
                                },
                                layout: {
                                    duration: 0.3,
                                    ease: 'easeOut'
                                }
                            }}
                            viewport={{
                                amount: 0.3,
                                once: true
                            }}
                            whileInView={{
                                opacity: 1,
                                y: 0
                            }}
                        >
                            <WaypointCard
                                description={waypoint.description}
                                distanceAriaLabel={t(
                                    'nearbyWaypoints.distanceLabel',
                                    {distance: waypoint.distanceLabel}
                                )}
                                distanceLabel={waypoint.distanceLabel}
                                href={waypoint.href}
                                imageAlt={waypoint.imageAlt}
                                imageUrl={waypoint.imageUrl}
                                number={waypoint.number}
                                testId={testId}
                                title={waypoint.title}
                                walkTitle={waypoint.walkTitle}
                            />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </List>
            {hasMore && (
                <LoadMoreButton
                    data-cy={`${testId}-load-more`}
                    disabled={isLoading}
                    type="button"
                    onClick={loadMore}
                >
                    {isLoading ? t('common.loading') : t('nearbyWaypoints.loadMore')}
                </LoadMoreButton>
            )}
        </Wrapper>
    );
};

NearbyWaypoints.displayName = 'NearbyWaypoints';

export {NearbyWaypoints};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.section`
    padding: ${spacing(4)} 0;
`;

const Header = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(4)};
    justify-content: space-between;
    margin-bottom: ${spacing(6)};
`;

const SectionHeading = styled(Headline)`
    color: ${({theme}) => theme.colors.primaryDefault};
`;

const ModeSwitchRow = styled.div`
    align-items: center;
    display: flex;
    flex-shrink: 0;
    gap: ${spacing(2)};
`;

const ModeLabel = styled.span`
    color: ${({theme}) => theme.colors.inkSecondary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    font-weight: 600;
`;

/** Props for the toggle styled components. */
interface ToggleProps {
    /** Whether the toggle is in the active/on state. */
    $active: boolean;
}

const ToggleTrack = styled.button<ToggleProps>`
    background: ${({$active, theme}) => ($active ? theme.colors.primaryDefault : theme.colors.surfaceDim)};
    border: none;
    border-radius: 14px;
    cursor: pointer;
    flex-shrink: 0;
    height: 28px;
    padding: 2px;
    position: relative;
    transition: background 0.2s ease;
    width: 48px;
`;

const ToggleThumb = styled.span<ToggleProps>`
    background: ${({theme}) => theme.colors.white};
    border-radius: 50%;
    display: block;
    height: 24px;
    transform: translateX(${({$active}) => ($active ? '20px' : '0')});
    transition: transform 0.2s ease;
    width: 24px;
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(3)};
`;

const LoadMoreButton = styled.button`
    appearance: none;
    background: ${({theme}) => theme.colors.surfaceHigh};
    border: none;
    border-radius: 9999px;
    color: ${({theme}) => theme.colors.inkSecondary};
    cursor: pointer;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    font-weight: 700;
    margin-top: ${spacing(4)};
    padding: ${spacing(3)} 0;
    text-align: center;
    width: 100%;

    &:disabled {
        cursor: default;
        opacity: 0.6;
    }

    &:hover:not(:disabled) {
        background: ${({theme}) => theme.colors.surfaceMid};
    }
`;